/**
 * The wallet: what you have, what you can withdraw, and what has happened.
 *
 * The design problem here is one number that is really three. A player thinks
 * they have "a balance"; the system has `available`, `locked` (staked in a live
 * round) and `withdrawable`. Since §9.10 removed the wagering gate, a player's
 * whole balance is withdrawable at any time — the 22% deposit cut, not a hold,
 * is what the platform takes (§3) — so `withdrawable` tracks `available`
 * exactly and the wallet shows the total plus what is currently locked in play.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { errorMessage } from '../api/errors.ts';
import type {
  BalanceView,
  DepositView,
  UserView,
  WalletTransactionView,
  WithdrawalView,
} from '../api/types.ts';
import { useT } from '../i18n/index.tsx';
import type { Messages } from '../i18n/messages.ts';
import { etb, isZero } from '../format.ts';

interface WalletProps {
  balance: BalanceView | null;
  user: UserView | null;
  onDeposit(): void;
  onWithdraw(): void;
  onPastRounds(): void;
  onRefreshBalance(): void;
}

/**
 * One timeline from three sources.
 *
 * Deposits and withdrawals keep their workflow rows — review status is
 * information the raw ledger does not carry. Everything else that moves the
 * balance (referral bonus, prize, refund, cartela purchase) comes straight
 * from `GET /me/transactions`, so a balance change is always explicable here:
 * no money arrives without its line.
 */
interface ActivityItem {
  id: string;
  kind: 'deposit' | 'withdrawal' | 'transaction';
  /** Which way the money moved — drives both the sign and the colour. */
  direction: 'in' | 'out';
  /** Unsigned santim amount; the sign lives in `direction`. */
  amount: string;
  /** The item's name: "Deposit", "Referral bonus", … */
  label: string;
  /** Workflow detail under the name (review state), when there is any. */
  status?: string;
  createdAt: string;
}

export function Wallet({
  balance,
  user,
  onDeposit,
  onWithdraw,
  onPastRounds,
  onRefreshBalance,
}: WalletProps) {
  const t = useT();
  const [activity, setActivity] = useState<ActivityItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [deposits, withdrawals, transactions] = await Promise.all([
        api.deposits(),
        api.withdrawals(),
        api.transactions(),
      ]);
      setActivity(
        mergeActivity(deposits.deposits, withdrawals.withdrawals, transactions.transactions, t),
      );
      setError(null);
    } catch (err) {
      setError(errorMessage(err, t));
    }
  }, [t]);

  useEffect(() => {
    void load();
    onRefreshBalance();
  }, [load, onRefreshBalance]);

  async function copyReferralLink(link: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be unavailable (permissions, non-secure
      // context) — the link is still shown and selectable, nothing is lost.
    }
  }

  return (
    <div className="screen">
      <h1 className="lobby__title">{t.walletTitle}</h1>

      {error && <div className="banner banner--error">{error}</div>}

      <section className="balancecard">
        <div className="balancecard__total">
          <span className="balancecard__label">{t.total}</span>
          <strong className="balancecard__value">{etb(balance?.available ?? null)}</strong>
        </div>

        <dl className="balancecard__rows">
          <div className="balancecard__row">
            <dt>{t.withdrawable}</dt>
            <dd>{etb(balance?.withdrawable ?? null)}</dd>
          </div>
          {/* Migration 066: bonus money is playable but withheld from the
              withdrawable figure above. Showing it here answers "why is my
              withdrawable less than my balance?" in the same glance, before
              it becomes a support question — and only when it exists, so a
              player without promotions sees exactly the card they always did. */}
          {balance && !isZero(balance.bonusLocked) && (
            <div className="balancecard__row">
              <dt>{t.bonusMoney}</dt>
              <dd>{etb(balance.bonusLocked)}</dd>
            </div>
          )}
          {balance && !isZero(balance.locked) && (
            <div className="balancecard__row">
              <dt>{t.lockedInPlay}</dt>
              <dd>{etb(balance.locked)}</dd>
            </div>
          )}
        </dl>
      </section>

      <div className="walletactions">
        <button type="button" className="button button--primary" onClick={onDeposit}>
          {t.deposit}
        </button>
        <button
          type="button"
          className="button"
          onClick={onWithdraw}
          disabled={!balance || isZero(balance.withdrawable)}
        >
          {t.withdraw}
        </button>
      </div>

      {user?.referralLink != null && (
        <InviteCard
          link={user.referralLink}
          copied={copied}
          onCopy={copyReferralLink}
          t={t}
        />
      )}

      <section className="activity">
        <div className="activity__titlerow">
          <h2 className="activity__title">{t.activity}</h2>
          <button type="button" className="activity__link" onClick={onPastRounds}>
            {t.pastRounds}
          </button>
        </div>
        {activity === null && <p className="activity__empty">{t.connecting}</p>}
        {activity?.length === 0 && <p className="activity__empty">{t.noActivity}</p>}
        <ul className="activity__list">
          {(activity ?? []).map((item) => (
            <li key={`${item.kind}-${item.id}`} className="activity__item">
              <div className="activity__main">
                <span className="activity__kind">{item.label}</span>
                {item.status !== undefined && (
                  <span className="activity__status">{item.status}</span>
                )}
              </div>
              <span className={`activity__amount activity__amount--${item.direction}`}>
                {item.direction === 'in' ? '+' : '−'}
                {etb(item.amount)}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/**
 * One timeline from three sources, newest first.
 *
 * A player does not think in terms of "deposits", "withdrawals" and "the
 * ledger" as separate systems; they think about what happened to their money
 * and when. Ledger entries for `deposit`/`withdrawal` kinds are dropped —
 * those movements are already represented by their workflow rows, which say
 * more (review status) than a bare posting would, and showing both would be
 * double-counting one event.
 */
function mergeActivity(
  deposits: DepositView[],
  withdrawals: WithdrawalView[],
  transactions: WalletTransactionView[],
  t: Messages,
): ActivityItem[] {
  const items: ActivityItem[] = [
    ...deposits.map((d) => ({
      id: d.id,
      kind: 'deposit' as const,
      direction: 'in' as const,
      amount: d.amount,
      label: t.deposit,
      status: depositStatusLabel(d.status, t),
      createdAt: d.createdAt,
    })),
    ...withdrawals.map((w) => ({
      id: w.id,
      kind: 'withdrawal' as const,
      direction: 'out' as const,
      amount: w.amount,
      label: t.withdraw,
      status: withdrawalStatusLabel(w.status, t, w.failureReason),
      createdAt: w.createdAt,
    })),
    ...transactions
      .filter((tx) => tx.kind !== 'deposit' && tx.kind !== 'withdrawal')
      .map((tx) => {
        // The sign is the server's record of direction. It is split out here
        // because `etb()` formats magnitudes only — a signed string would
        // fail to parse, not display wrong, so this cannot silently mislabel.
        const out = tx.amount.startsWith('-');
        return {
          id: tx.id,
          kind: 'transaction' as const,
          direction: out ? ('out' as const) : ('in' as const),
          amount: out ? tx.amount.slice(1) : tx.amount,
          label: transactionLabel(tx.kind, t),
          ...(tx.status === 'reversed' ? { status: t.statusReversed } : {}),
          createdAt: tx.createdAt,
        };
      }),
  ];
  return items.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function transactionLabel(kind: WalletTransactionView['kind'], t: Messages): string {
  switch (kind) {
    case 'referral_reward':
      return t.txReferralReward;
    case 'welcome_bonus':
      return t.txWelcomeBonus;
    case 'payout':
      return t.txPrize;
    case 'refund':
      return t.txRefund;
    case 'cartela_purchase':
      return t.txCartelaPurchase;
    case 'commission':
      return t.txCommission;
    case 'adjustment':
      return t.txAdjustment;
    case 'round_stake':
    case 'deposit':
    case 'withdrawal':
      return t.txOther;
  }
}

function InviteCard({
  link,
  copied,
  onCopy,
  t,
}: {
  link: string;
  copied: boolean;
  onCopy: (link: string) => void;
  t: Messages;
}) {
  return (
    <section className="invitecard">
      <h2 className="invitecard__title">{t.inviteTitle}</h2>
      <p className="invitecard__body">{t.inviteBody}</p>
      <div className="invitecard__row">
        <input
          className="input invitecard__link"
          value={link}
          readOnly
          onFocus={(e) => e.currentTarget.select()}
        />
        <button type="button" className="button button--primary" onClick={() => onCopy(link)}>
          {copied ? t.inviteCopied : t.inviteCopy}
        </button>
      </div>
    </section>
  );
}

function depositStatusLabel(status: DepositView['status'], t: Messages): string {
  switch (status) {
    case 'submitted':
      return t.statusSubmitted;
    case 'under_review':
      return t.statusUnderReview;
    case 'approved':
      return t.statusApproved;
    case 'rejected':
      return t.statusRejected;
  }
}

/**
 * `failureReason` carries a code, not a sentence — see `WithdrawalFailureCode`
 * on the API side. A payout the dispatcher gave up on because telebirr would
 * not accept the destination says so, because that is the one failure a player
 * can fix themselves by requesting again with the right number. Every other
 * way a transfer can fail reads "Error": true, and all this screen can
 * honestly claim.
 */
function withdrawalStatusLabel(
  status: WithdrawalView['status'],
  t: Messages,
  failureReason?: string | null,
): string {
  switch (status) {
    case 'requested':
      return t.statusRequested;
    case 'approved':
      return t.statusUnderReview;
    case 'processing':
      return t.statusProcessing;
    // Two ways a payout reaches the same end — an admin confirming it, or the
    // bank's own SMS doing so. The difference is bookkeeping, and a player has
    // no use for it: their money was sent either way.
    case 'completed':
    case 'confirmed':
      return t.statusCompleted;
    case 'failed':
      if (failureReason === 'wrong_number') return t.statusWrongNumber;
      return failureReason ? t.statusError : t.statusFailed;
    case 'rejected':
      return t.statusRejected;
  }
}
